/**
 * Экран «забыли пароль» `/forgot` (Фаза 3, И3).
 *
 * Форма `{email}` → [`requestPasswordReset`](../auth.ts) (`POST /api/auth/forgot`).
 * Безопасность: сервер ВСЕГДА отвечает 200 и не раскрывает существование email,
 * поэтому после запроса показываем единое «если аккаунт существует, инструкция
 * отправлена» — независимо от того, найден ли email. 429 — дружелюбное «позже».
 */
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, CAPTCHA_FAILED, type CaptchaSolution } from '../api';
import { Captcha } from '../components/Captcha';
import { Link } from '../components/ui';
import { requestPasswordReset } from '../auth';

export function Forgot(): JSX.Element {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Слайд-капча (И8): обязательна (анти email-flooding). nonce → перевыпуск после ошибки.
  const [captcha, setCaptcha] = useState<CaptchaSolution | null>(null);
  const [captchaNonce, setCaptchaNonce] = useState(0);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      await requestPasswordReset(email.trim(), captcha ?? undefined);
      // Успех — показываем единое сообщение (существование email не раскрываем).
      setDone(true);
    } catch (e) {
      if (e instanceof ApiError && e.message === CAPTCHA_FAILED) {
        // Капча одноразовая — перевыпускаем новую (remount) и сбрасываем решение.
        setCaptchaNonce((n) => n + 1);
        setCaptcha(null);
        setError(t('errors.captchaFailed'));
      } else if (e instanceof ApiError && e.status === 429) {
        setError(t('errors.tooManyRequests'));
      } else {
        setError(t('errors.forgotNetwork'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="card card--page" aria-labelledby="forgot-title">
      <h1 id="forgot-title" className="page-title">
        {t('forgot.title')}
      </h1>

      {done ? (
        <p className="muted" data-testid="forgot-done">
          {t('forgot.done')}
        </p>
      ) : (
        <form className="auth-form" onSubmit={handleSubmit} noValidate>
          <p className="muted">{t('forgot.hint')}</p>

          <div className="form-row">
            <label className="field-label" htmlFor="forgot-email">
              {t('common.email')}
            </label>
            <input
              id="forgot-email"
              className="text-input"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
            />
          </div>

          {error ? (
            <p className="error" role="alert" data-testid="forgot-error">
              {error}
            </p>
          ) : null}

          {/* Слайд-капча (И8): обязательна (анти email-flooding). key=nonce →
              перевыпуск новой после ошибки (капча одноразовая). */}
          <Captcha key={captchaNonce} onChange={setCaptcha} />

          <button type="submit" className="cta" disabled={submitting} data-testid="forgot-submit">
            {submitting ? t('common.submitting') : t('forgot.submit')}
          </button>
        </form>
      )}

      <p className="auth-switch">
        <Link to="/login">{t('forgot.backToLogin')}</Link>
      </p>
      <p className="auth-switch">
        <Link to="/">{t('nav.toMain')}</Link>
      </p>
    </main>
  );
}
